// Composable for stacked bar chart using Chart.js.
// Mounts a Chart.js instance on a canvas ref. Handles context overlay and cleanup.

import { watch, onUnmounted, shallowRef } from 'vue'
import { Chart, BarController, BarElement,
  CategoryScale, LinearScale, Tooltip, Legend } from 'chart.js'
import { PROJECT_COLORS, NO_PROJECT_COLOR, CONTEXT_COLOR, OTHER_COLOR } from '../utils/chartColors'

Chart.register(BarController, BarElement,
  CategoryScale, LinearScale, Tooltip, Legend)

// Plugin that draws context time as true background rectangles, before any bars.
const contextBackdropPlugin = {
  id: 'contextBackdrop',
  beforeDatasetsDraw(chart) {
    const meta = chart.options.plugins?.contextBackdrop
    if (!meta?.enabled || !meta.data?.length) return
    const { ctx, chartArea, scales } = chart
    const xScale = scales.x
    const yScale = scales.y
    ctx.save()
    ctx.fillStyle = meta.color
    meta.data.forEach((value, i) => {
      if (value <= 0) return
      const xCenter = xScale.getPixelForValue(i)
      const bandwidth = xScale.width / (xScale.ticks.length || 1)
      // Match the project bars: barPercentage 0.65 × categoryPercentage 0.7
      const barWidth = bandwidth * 0.65 * 0.7
      const x = xCenter - barWidth / 2
      const w = barWidth
      const y = yScale.getPixelForValue(value)
      const h = chartArea.bottom - y
      if (h > 0) {
        ctx.beginPath()
        ctx.roundRect(x, y, w, h, 4)
        ctx.fill()
      }
    })
    ctx.restore()
  },
}
Chart.register(contextBackdropPlugin)

function secondsToHours(s) { return s / 3600 }

function fmtHours(h) {
  let hrs = Math.floor(h)
  let mins = Math.round((h - hrs) * 60)
  if (mins === 60) { hrs++; mins = 0 }
  return `${hrs}h ${mins}m`
}

function assignColor(name, index) {
  if (name === 'No project' || name === 'Untagged') return NO_PROJECT_COLOR
  return index < PROJECT_COLORS.length ? PROJECT_COLORS[index] : OTHER_COLOR
}

function niceMax(values) {
  const max = Math.max(...values, 0)
  if (max <= 0) return 2
  const ceil = Math.ceil(max)
  if (ceil <= 6) return ceil % 2 === 0 ? ceil : ceil + 1
  return Math.ceil(ceil / 2) * 2
}

export function useAnalyticsChart(canvasRef, dataRef, chartModeRef, colorMapsRef = null) {
  const chart = shallowRef(null)

  function sharedScales(yMax) {
    return {
      x: {
        stacked: true,
        grid: { display: false },
        ticks: { font: { size: 11 }, color: '#999' },
      },
      y: {
        stacked: true,
        max: yMax,
        ticks: {
          stepSize: yMax / 2,
          callback: (v) => `${Math.round(v)}h`,
          font: { size: 11 },
          color: '#999',
        },
        grid: { color: 'rgba(0,0,0,0.05)' },
      },
    }
  }

  function buildChart() {
    const canvas = canvasRef.value
    const data = dataRef.value

    if (chart.value) {
      try {
        chart.value.destroy()
      } catch (_) {
        // ignore errors from already-destroyed chart instances
      }
      chart.value = null
    }

    if (!canvas || !data?.time_series?.length) return

    const mode = chartModeRef.value  // 'project' | 'context' | 'all'
    const labels = data.time_series.map(b => b.label)

    // --- context mode: stacked bars per context label ---
    if (mode === 'context') {
      const contextSet = new Map()
      data.time_series.forEach(bucket => {
        (bucket.contexts || []).forEach(c => {
          if (!contextSet.has(c.name)) contextSet.set(c.name, contextSet.size)
        })
      })

      const contextNames = [...contextSet.keys()]
      const datasets = contextNames.map((name, i) => {
        const color = colorMapsRef?.value?.context?.[name] ?? assignColor(name, i)
        return {
          label: name,
          data: data.time_series.map(b => {
            const c = (b.contexts || []).find(x => x.name === name)
            return c ? secondsToHours(c.seconds) : 0
          }),
          backgroundColor: color,
          borderColor: color,
          borderWidth: 0,
          borderRadius: 3,
          barPercentage: 0.65,
          categoryPercentage: 0.7,
          stack: 'contexts',
        }
      })

      const stackedTotals = labels.map((_, i) =>
        datasets.reduce((sum, d) => sum + (Number(d.data[i]) || 0), 0)
      )
      const yMax = niceMax(stackedTotals)

      chart.value = new Chart(canvas, {
        type: 'bar',
        data: { labels, datasets },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          interaction: { mode: 'index', intersect: false },
          plugins: {
            legend: { display: false },
            contextBackdrop: { enabled: false },
            tooltip: {
              callbacks: {
                label: (ctx) => {
                  const h = ctx.parsed.y
                  if (h <= 0) return null
                  return `${ctx.dataset.label}: ${fmtHours(h)}`
                },
              },
            },
          },
          scales: sharedScales(yMax),
        },
      })
      return
    }

    // --- project / all mode: stacked project bars, optional context backdrop ---
    const showCtx = mode === 'all'

    const projectSet = new Map()
    data.time_series.forEach(bucket => {
      (bucket.projects || []).forEach(p => {
        if (!projectSet.has(p.name)) projectSet.set(p.name, projectSet.size)
      })
    })

    // "No project" rendered on top of stack
    const projectNames = [...projectSet.keys()].sort((a, b) => {
      if (a === 'No project') return 1
      if (b === 'No project') return -1
      return 0
    })

    const contextData = data.time_series.map(b => secondsToHours(b.context_seconds))
    const contextBreakdown = data.time_series.map(b => b.contexts || [])
    const datasets = []

    projectNames.forEach((name, i) => {
      const color = colorMapsRef?.value?.project?.[name] ?? assignColor(name, i)
      datasets.push({
        label: name,
        data: data.time_series.map(b => {
          const p = (b.projects || []).find(x => x.name === name)
          return p ? secondsToHours(p.seconds) : 0
        }),
        backgroundColor: color,
        borderColor: color,
        borderWidth: 0,
        borderRadius: 3,
        barPercentage: 0.65,
        categoryPercentage: 0.7,
        stack: 'events',
      })
    })

    const projectValues = datasets.flatMap(d => d.data)
    const stackedTotals = labels.map((_, i) =>
      datasets.reduce((sum, d) => sum + (Number(d.data[i]) || 0), 0)
    )
    const yMax = niceMax(showCtx ? [...stackedTotals, ...contextData] : stackedTotals)

    chart.value = new Chart(canvas, {
      type: 'bar',
      data: { labels, datasets },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: 'index', intersect: false },
        plugins: {
          legend: { display: false },
          contextBackdrop: {
            enabled: showCtx,
            data: contextData,
            color: CONTEXT_COLOR,
          },
          tooltip: {
            callbacks: {
              label: (ctx) => {
                const h = ctx.parsed.y
                if (h <= 0) return null
                const hrs = Math.floor(h)
                const mins = Math.round((h - hrs) * 60)
                return `${ctx.dataset.label}: ${hrs}h ${mins}m`
              },
              footer: (items) => {
                if (!showCtx || !items.length) return undefined
                const contexts = contextBreakdown[items[0].dataIndex]
                if (!contexts?.length) return undefined
                return contexts.map(c => {
                  const h = secondsToHours(c.seconds)
                  return `${c.name}: ${fmtHours(h)}`
                })
              },
            },
          },
        },
        scales: sharedScales(yMax),
      },
    })
  }

  watch([dataRef, chartModeRef], buildChart, { deep: true, flush: 'post' })

  onUnmounted(() => {
    if (chart.value) {
      try {
        chart.value.destroy()
      } catch (_) {
        // ignore
      }
      chart.value = null
    }
  })

  return { chart, rebuild: buildChart }
}

